/**
 * Matrix transformation utilities for grid configurations
 * 
 * Transforms flat configuration arrays into 2D matrices organized by margin values
 * for intuitive selection similar to flight booking interfaces.
 */

/**
 * Transform configurations into a 2D matrix structure
 * @param {Array<Object>} configurations - Array of grid configurations
 * @returns {Object} Matrix data structure with axes and lookup methods
 */
export function transformToMatrix(configurations) {
  if (!configurations || configurations.length === 0) {
    return createEmptyMatrix();
  }

  // Extract unique margin values and sort them (tightest to loosest)
  const horizontalMargins = getUniqueMargins(configurations, 'leftMargin');
  const verticalMargins = getUniqueMargins(configurations, 'topMargin');

  // Create 2D matrix indexed by margin values
  const matrix = createMatrixGrid(configurations, horizontalMargins, verticalMargins);

  // Create axis metadata for UI rendering
  const xAxis = horizontalMargins.map((margin, index) => ({
    index,
    margin,
    label: formatMarginLabel(margin),
    count: getConfigurationsForHorizontalMargin(configurations, margin).length
  }));

  const yAxis = verticalMargins.map((margin, index) => ({
    index,
    margin,
    label: formatMarginLabel(margin),
    count: getConfigurationsForVerticalMargin(configurations, margin).length
  }));

  return {
    matrix,
    xAxis, // Horizontal margins (left to right: tightest to loosest)
    yAxis, // Vertical margins (top to bottom: tightest to loosest)
    dimensions: {
      width: horizontalMargins.length,
      height: verticalMargins.length
    },
    totalConfigurations: configurations.length,
    
    // Lookup methods
    getCellContent: (xIndex, yIndex) => getCellContent(matrix, xIndex, yIndex),
    getConfigurationsByMargins: (hMargin, vMargin) => getConfigurationsByMargins(configurations, hMargin, vMargin),
    getAxisLabels: () => ({ x: xAxis, y: yAxis })
  };
}

/**
 * Extract unique margin values from configurations and sort them
 * @param {Array<Object>} configurations - Configuration array
 * @param {string} marginKey - Property key ('leftMargin' or 'topMargin')
 * @returns {Array<number>} Sorted unique margin values (tightest to loosest)
 */
function getUniqueMargins(configurations, marginKey) {
  const margins = [...new Set(configurations.map(config => config[marginKey]))];
  return margins.sort((a, b) => a - b); // Ascending: tightest to loosest
}

/**
 * Create a 2D matrix grid with configurations placed at their margin coordinates
 * @param {Array<Object>} configurations - Configuration array
 * @param {Array<number>} horizontalMargins - Sorted horizontal margin values
 * @param {Array<number>} verticalMargins - Sorted vertical margin values  
 * @returns {Array<Array<Object>>} 2D matrix grid
 */
function createMatrixGrid(configurations, horizontalMargins, verticalMargins) {
  const matrix = [];
  
  // Initialize empty matrix
  for (let y = 0; y < verticalMargins.length; y++) {
    matrix[y] = new Array(horizontalMargins.length).fill(null);
  }
  
  // Group configurations by their exact margin values
  const configsByMargins = new Map();
  configurations.forEach(config => {
    const key = `${config.leftMargin},${config.topMargin}`;
    if (!configsByMargins.has(key)) {
      configsByMargins.set(key, []);
    }
    configsByMargins.get(key).push(config);
  });
  
  // Place configuration groups in matrix cells
  for (let y = 0; y < verticalMargins.length; y++) {
    for (let x = 0; x < horizontalMargins.length; x++) {
      const hMargin = horizontalMargins[x];
      const vMargin = verticalMargins[y];
      const key = `${hMargin},${vMargin}`;
      
      if (configsByMargins.has(key)) {
        const configs = configsByMargins.get(key);
        matrix[y][x] = {
          horizontalMargin: hMargin,
          verticalMargin: vMargin,
          configurations: configs,
          count: configs.length,
          // Store the best configuration (first one, as they're pre-sorted)
          primaryConfig: configs[0]
        };
      }
    }
  }
  
  return matrix;
}

/**
 * Get content for a specific matrix cell
 * @param {Array<Array<Object>>} matrix - 2D matrix
 * @param {number} xIndex - X coordinate (horizontal margin index)
 * @param {number} yIndex - Y coordinate (vertical margin index)
 * @returns {Object|null} Cell content or null if empty
 */
function getCellContent(matrix, xIndex, yIndex) {
  if (yIndex >= 0 && yIndex < matrix.length && 
      xIndex >= 0 && xIndex < matrix[yIndex].length) {
    return matrix[yIndex][xIndex];
  }
  return null;
}

/**
 * Get configurations matching specific margin values
 * @param {Array<Object>} configurations - Configuration array
 * @param {number} horizontalMargin - Target horizontal margin
 * @param {number} verticalMargin - Target vertical margin
 * @returns {Array<Object>} Matching configurations
 */
function getConfigurationsByMargins(configurations, horizontalMargin, verticalMargin) {
  return configurations.filter(config => 
    Math.abs(config.leftMargin - horizontalMargin) < 0.001 &&
    Math.abs(config.topMargin - verticalMargin) < 0.001
  );
}

/**
 * Get all configurations with a specific horizontal margin
 * @param {Array<Object>} configurations - Configuration array
 * @param {number} margin - Target horizontal margin
 * @returns {Array<Object>} Matching configurations
 */
function getConfigurationsForHorizontalMargin(configurations, margin) {
  return configurations.filter(config => Math.abs(config.leftMargin - margin) < 0.001);
}

/**
 * Get all configurations with a specific vertical margin
 * @param {Array<Object>} configurations - Configuration array
 * @param {number} margin - Target vertical margin
 * @returns {Array<Object>} Matching configurations
 */
function getConfigurationsForVerticalMargin(configurations, margin) {
  return configurations.filter(config => Math.abs(config.topMargin - margin) < 0.001);
}

/**
 * Format margin value for display
 * @param {number} margin - Margin value
 * @returns {string} Formatted label
 */
function formatMarginLabel(margin) {
  if (margin % 1 === 0) {
    return margin.toString();
  }
  return margin.toFixed(3).replace(/\.?0+$/, '');
}

/**
 * Create empty matrix structure
 * @returns {Object} Empty matrix data structure
 */
function createEmptyMatrix() {
  return {
    matrix: [],
    xAxis: [],
    yAxis: [],
    dimensions: { width: 0, height: 0 },
    totalConfigurations: 0,
    getCellContent: () => null,
    getConfigurationsByMargins: () => [],
    getAxisLabels: () => ({ x: [], y: [] })
  };
}

/**
 * Get matrix statistics for debugging and UI info
 * @param {Object} matrixData - Matrix data structure
 * @returns {Object} Statistics about the matrix
 */
export function getMatrixStatistics(matrixData) {
  const { matrix, totalConfigurations } = matrixData;
  let populatedCells = 0;
  let maxConfigsPerCell = 0;
  let totalCellConfigs = 0;

  matrix.forEach(row => {
    row.forEach(cell => {
      if (cell && cell.count > 0) {
        populatedCells++;
        maxConfigsPerCell = Math.max(maxConfigsPerCell, cell.count);
        totalCellConfigs += cell.count;
      }
    });
  });

  return {
    totalCells: matrix.length * (matrix[0] ? matrix[0].length : 0),
    populatedCells,
    emptyCells: (matrix.length * (matrix[0] ? matrix[0].length : 0)) - populatedCells,
    fillRate: matrix.length > 0 ? (populatedCells / (matrix.length * matrix[0].length)) : 0,
    maxConfigurationsPerCell: maxConfigsPerCell,
    totalConfigurations,
    averageConfigsPerPopulatedCell: populatedCells > 0 ? totalCellConfigs / populatedCells : 0
  };
}